import { useEffect, useRef, useState } from "react";

/** Petites horloges d'animation des couches de la carte (docs/14 §2.10) : les transitions de peinture du moteur
 *  ne s'appliquent pas aux propriétés pilotées par les données, les opacités par entité suivent donc
 *  `requestAnimationFrame`. Mouvement réduit demandé par le poste : passage direct. */

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function canAnimate(): boolean {
  return typeof requestAnimationFrame === "function" && !prefersReducedMotion();
}

/** Horloge `performance.now()` relevée à chaque image tant que `active` est vrai (sinon figée) : un composant qui
 *  l'utilise se redessine à chaque image le temps d'une animation, puis plus du tout. */
export function useTicker(active: boolean): number {
  const [now, setNow] = useState(() => (typeof performance !== "undefined" ? performance.now() : 0));
  useEffect(() => {
    if (!active || typeof requestAnimationFrame !== "function") return;
    let frame = requestAnimationFrame(function tick(time) {
      setNow(time);
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [active]);
  return active ? now : typeof performance !== "undefined" ? performance.now() : now;
}

/** Valeur qui rejoint `target` en `durationMs` (courbe douce), depuis la valeur affichée ; directe en mouvement
 *  réduit. */
export function useEased(target: number, durationMs: number): number {
  const [value, setValue] = useState(target);
  const shown = useRef(target);
  useEffect(() => {
    if (!canAnimate() || durationMs <= 0) {
      shown.current = target;
      setValue(target);
      return;
    }
    const from = shown.current;
    if (from === target) return;
    const started = performance.now();
    let frame = requestAnimationFrame(function tick(time) {
      const raw = Math.min(1, Math.max(0, (time - started) / durationMs));
      const eased = raw * raw * (3 - 2 * raw);
      shown.current = from + (target - from) * eased;
      setValue(shown.current);
      if (raw < 1) frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs]);
  return value;
}

/** Vrai tant que `on` l'est, et encore `exitMs` après (couches gardées montées à opacité nulle pendant leur
 *  sortie, `MAP_EXIT_MS`). */
export function useLingering(on: boolean, exitMs: number): boolean {
  const [lingering, setLingering] = useState(on);
  useEffect(() => {
    if (on) {
      setLingering(true);
      return;
    }
    const timer = window.setTimeout(() => setLingering(false), exitMs);
    return () => window.clearTimeout(timer);
  }, [on, exitMs]);
  return on || lingering;
}
